<template>
    <div class="questionnaire-step-final">
        <div class="content-wrapper">
            <div class="content-container">
                <Transition
                    appear
                    mode="out-in"
                    name="form"
                >
                    <div
                        v-if="isShowingLoader"
                        class="loader form"
                    >
                        <LoadingAnimation />
                    </div>

                    <QuestionnaireAuth
                        v-else
                        ref="auth"
                        :errors
                        :fetching-new-customer-submit="fetching"
                        :sign-up-step
                        @login-success="handleLoginSuccess"
                        @submit-new-customer="submitNewCustomer"
                        @page-email="emit('page-email', $event)"
                    />
                </Transition>
            </div>
            <div class="image-container">
                <Transition
                    appear
                    name="pill"
                >
                    <div class="image-wrapper">
                        <div class="rounded-image">
                            <CmsImage
                                :data="signUpStep.image"
                                fit="cover"
                                is-eager
                            />
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, toRef, useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'

import type {
    QuestionnaireFragment,
    QuestionnaireSignUpStepFragment,
    QuestionnaireStepFinalQuery,
    QuestionnaireStepFinalQueryVariables,
} from '@/graphql/api'

import { useQuestionnaireSubmit } from './useQuestionnaireSubmit'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import {
    useLastResponseErrorAtPath,
    isAuthorizationError,
} from '@/helpers/errors'
import { cmsColorToButtonColor, useCmsLinkItemAction } from '../..'
import { useQuestionnaireStore } from './useQuestionnaireStore'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useAuth } from '@/plugins/auth.ts'
import { useQuestionnaireCompleteNewCustomerMutation } from '@/graphql/api/mutations/questionnaireCompleteNewCustomer.mutation.generated.ts'
import { useQuestionnaireCompleteExistingCustomerMutation } from '@/graphql/api/mutations/questionnaireCompleteExistingCustomer.mutation.generated.ts'

import CmsImage from '@/components/cms/CmsImage.vue'
import QuestionnaireAuth from './QuestionnaireAuth.vue'
import { useGraphqlClient } from '@/plugins/graphql-client.ts'
import { QuestionnaireStepFinalDocument } from '@/components/cms/component/questionnaire/QuestionnaireStepFinal.generated.ts'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'

const { signUpStep, questionnaire, sourceUrl } = defineProps<{
    signUpStep: QuestionnaireSignUpStepFragment
    questionnaire: QuestionnaireFragment
    sourceUrl: string
}>()

const emit = defineEmits<{
    'submit': []
    'page-email': [boolean]
}>()

const newCustomerMutation = useQuestionnaireCompleteNewCustomerMutation()
const { executeMutation: executeMutationNewCustomer, fetching } =
    newCustomerMutation

const existingCustomerMutation =
    useQuestionnaireCompleteExistingCustomerMutation()
const { executeMutation: executeMutationExistingCustomer } =
    existingCustomerMutation

const errors = useLastResponseErrorAtPath([
    {
        response: newCustomerMutation,
        path: 'questionnaireCompleteNewCustomer',
    },
    {
        response: existingCustomerMutation,
        path: 'questionnaireCompleteExistingCustomer',
    },
])

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const questionnaireCompleteInput = useQuestionnaireSubmit(
    toRef(() => questionnaire),
    sourceUrl
)

const { clearQuestionnaireAnswers } = useQuestionnaireStore()

const client = useGraphqlClient()

const cmsLinkItemAction = useCmsLinkItemAction()

const redirectAfterQuestionnaireComplete = async () => {
    // Let the parent know first so that
    // currentStepIndex reactivity is disabled and no intermediate
    // blinking of step 0 occurs
    emit('submit')

    // Answers are persisted on submit - so we need to refetch questionnaire so redirect link is filled with latest data.
    // Probly it's better to return this on mutation response, but its a bit too complicated because Redirect Link lives inside CMS
    // but actual data where to redirect lives in monolith.
    // And because of that separation: following happens on submit.
    // Gateway fetches CMS and populates data to questionnaire input
    // Then calls monolith.
    // Then it calls fetches CMS again, because of redirect link
    // Then it calls again monolith, because of redirect link data.
    const freshQuestionnaire = await client.value
        .query<
            QuestionnaireStepFinalQuery,
            QuestionnaireStepFinalQueryVariables
        >(QuestionnaireStepFinalDocument, {
            id: questionnaire.id,
        })
        .toPromise()

    if (freshQuestionnaire.data?.questionnaire?.redirectLink) {
        await cmsLinkItemAction(
            freshQuestionnaire.data?.questionnaire?.redirectLink
        )
    }

    clearQuestionnaireAnswers(questionnaire.id)
}

const { isLogged, setAuth } = useAuth()

const isAdmin = useIsAdmin()

const isShowingLoader = ref(false)

const canSubmitAsExistingCustomer = computed(
    (): boolean => Boolean(questionnaireCompleteInput.value) && isLogged.value
)

onMounted(async () => {
    if (!canSubmitAsExistingCustomer.value) {
        return
    }

    isShowingLoader.value = true
    await submitExistingCustomerAndComplete()
})

const submitExistingCustomerAndComplete = async () => {
    const response = await executeMutationExistingCustomer({
        input: questionnaireCompleteInput.value,
    })

    if (response.data?.questionnaireCompleteExistingCustomer) {
        await redirectAfterQuestionnaireComplete()

        return
    }

    if (
        response.error?.graphQLErrors.some(isAuthorizationError) ||
        !isLogged.value
    ) {
        isShowingLoader.value = false
    }
}

const handleLoginSuccess = () => submitExistingCustomerAndComplete()

const submitNewCustomer = async (successCallback: () => void) => {
    const input = questionnaireCompleteInput.value
    const customer = customerRegistrationInput.value

    if (!input || !customer) {
        return
    }

    const response = await executeMutationNewCustomer({
        input,
        customer,
        isConsultant: isAdmin.value,
    })

    const registeredCustomer =
        response.data?.questionnaireCompleteNewCustomer.customer

    if (!registeredCustomer) {
        return
    }

    setAuth(registeredCustomer.login)
    successCallback()
    await redirectAfterQuestionnaireComplete()
}

const imageBackgroundColor = computed(() =>
    cmsColorToButtonColor(signUpStep.backgroundColor)
)

const componentAuth = useTemplateRef('auth')

defineExpose({
    goToPageSso: () => componentAuth.value?.goToPageSoo(),
})
</script>

<style lang="scss" scoped>
@property --s {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

@property --x {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

@property --y {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

.questionnaire-step-final {
    width: 100%;
    position: absolute;
    inset: 0;
    height: 100%;
    overflow: auto;
    background: $stone-3;

    .content-wrapper {
        width: 100%;
        min-height: 100%;
        position: relative;
        display: flex;
        overflow: hidden;

        .content-container {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .loader {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .image-container {
            width: 100%;
            display: none;

            @include min-width(1024) {
                display: block;
            }

            .image-wrapper {
                width: 100%;
                height: 100%;
                background-image: linear-gradient(
                    90deg,
                    v-bind(imageBackgroundColor),
                    v-bind(imageBackgroundColor)
                );
                background-position: left 168px top 0;
                background-repeat: no-repeat;
                display: flex;
                align-items: center;
            }

            .rounded-image {
                width: 336px;
                max-width: 100%;
                height: 500px;
                border-radius: 200px;
                overflow: hidden;
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center center;
                position: relative;
                z-index: 2;
            }
        }
    }
}

// This step has custom transitions
.pill-enter-active,
.pill-leave-active {
    transition: all 0.85s var(--timing-function-easing-decelerate);
}

.pill-enter-from,
.pill-leave-to {
    opacity: 0;
    transform: translateX(40px);
}

.form-enter-active,
.form-leave-active {
    transition: all 0.95s var(--timing-function-easing-decelerate) 0.35s;
}

.form-enter-from,
.form-leave-to {
    opacity: 0;
    transform: translateY(40px);
}
</style>
